import React, { useMemo } from "react";
import { Empty, Spin, Segmented } from "antd";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import dayjs from "dayjs";

const METRICS = [
  { key: "heartRate", label: "心率", unit: "bpm", color: "#ff4d4f" },
  { key: "bloodOxygen", label: "血氧", unit: "%", color: "#1890ff" },
  { key: "bodyTemperature", label: "體溫", unit: "°C", color: "#52c41a" },
];

const formatTime = (t) => dayjs(t).format("HH:mm:ss");

// One chart at a time (Segmented picks the metric) rather than three stacked
// charts, to keep the tab legible inside a modal panel. Data is already
// downsampled server-side (min/max/avg per bucket) -- rendered as-is, no
// client-side resampling.
const VitalsTab = ({ series, loading, subjectId, coarsePlayheadMs, onChartSeek }) => {
  const [metric, setMetric] = React.useState("heartRate");
  const config = METRICS.find((m) => m.key === metric);

  const chartData = useMemo(
    () =>
      (series || []).map((point) => ({
        t: point.t,
        avg: point[metric]?.avg ?? null,
        min: point[metric]?.min ?? null,
        max: point[metric]?.max ?? null,
        band: point[metric] ? [point[metric].min, point[metric].max] : null,
      })),
    [series, metric],
  );

  if (subjectId == null) {
    return <Empty description="請先選擇人員" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  if (!loading && chartData.length === 0) {
    return <Empty description="此範圍內無生理數據" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Spin spinning={loading}>
      <Segmented
        size="small"
        value={metric}
        onChange={setMetric}
        options={METRICS.map((m) => ({ label: m.label, value: m.key }))}
        style={{ marginBottom: 12 }}
      />
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart
          data={chartData}
          onClick={(state) => {
            // activeLabel is the numeric x (t) under the cursor on a
            // type="number" axis; activePayload only exists after recharts
            // has processed a mousemove, so prefer the label.
            const t = state?.activeLabel ?? state?.activePayload?.[0]?.payload?.t;
            if (t != null && onChartSeek) onChartSeek(t);
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          {/* type="number": a categorical t-axis would space buckets evenly
              regardless of real gaps AND silently drop the playhead
              ReferenceLine (x only matches exact category values). */}
          <XAxis
            dataKey="t"
            type="number"
            domain={["dataMin", "dataMax"]}
            tickFormatter={formatTime}
            minTickGap={40}
          />
          <YAxis
            domain={["dataMin - 1", "dataMax + 1"]}
            label={{ value: config.unit, angle: -90, position: "insideLeft" }}
          />
          <Tooltip
            labelFormatter={(t) => dayjs(t).format("YYYY-MM-DD HH:mm:ss")}
            // One human row: average with its unit, plus the bucket's
            // min-max spread when it actually spreads. The band Area is
            // excluded via tooltipType="none" -- its [min, max] dataKey
            // otherwise renders raw ("8585") under its internal name.
            formatter={(value, name, item) => {
              if (name !== "avg") return [value, name];
              const point = item?.payload || {};
              const spread =
                point.min != null && point.max != null && point.min !== point.max
                  ? `（區間 ${point.min}–${point.max}）`
                  : "";
              const avg = Math.round(value * 10) / 10;
              return [`${avg} ${config.unit}${spread}`, config.label];
            }}
          />
          <Area
            dataKey="band"
            stroke="none"
            fill={config.color}
            fillOpacity={0.12}
            isAnimationActive={false}
            tooltipType="none"
          />
          <Line
            type="monotone"
            dataKey="avg"
            stroke={config.color}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
          {coarsePlayheadMs != null && (
            <ReferenceLine x={coarsePlayheadMs} stroke="#722ed1" strokeWidth={2} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Spin>
  );
};

export default VitalsTab;
